/**
 * The measurements a serving size can be given in, and the reading of one out
 * of the words on a packet.
 *
 * It lives in server/ for the same reason nutrients.ts does: both halves of
 * the app need the list, and the front end can import from here but not the
 * other way round. It must stay free of imports.
 */
export const SERVING_UNITS = ["g", "ml", "oz", "fl oz", "cup", "tbsp", "tsp", "piece"] as const;

export type ServingUnit = (typeof SERVING_UNITS)[number];

/** Every way a packet is likely to spell each unit. */
const SPELLINGS: Record<ServingUnit, string> = {
  g: "grams?|g",
  ml: "millilit(?:er|re)s?|ml",
  oz: "ounces?|oz",
  "fl oz": "fl\\.?\\s*oz|fluid\\s+ounces?",
  cup: "cups?",
  tbsp: "tablespoons?|tbsps?",
  tsp: "teaspoons?|tsps?",
  piece: "pieces?",
};

// Only a number directly followed by a known unit counts, so "2 biscuits (30 g)"
// is read as 30 g.
const SERVING = new RegExp(
  `(\\d+\\s+\\d+/\\d+|\\d+/\\d+|\\d+(?:[.,]\\d+)?)\\s*(${Object.values(SPELLINGS).join("|")})\\b`,
  "i",
);

/** "1 1/2", "2/3" or "1,5" as a number. */
function parseAmount(text: string): number {
  const [whole, fraction] = text.includes("/")
    ? text.includes(" ")
      ? text.split(/\s+/)
      : ["0", text]
    : [text.replace(",", "."), "0/1"];
  const [top, bottom] = fraction!.split("/").map(Number);
  return Number(whole) + top! / bottom!;
}

/**
 * Finds the first amount and unit in something like "1 cup (228g)".
 *
 * Returns null for anything it can't be sure of, such as "1 bar", so the form
 * is left for Sam to fill in rather than guessed at.
 */
export function readServing(text: string | null | undefined): { amount: number; unit: ServingUnit } | null {
  const match = text?.match(SERVING);
  if (!match) return null;
  const amount = parseAmount(match[1]!);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const word = match[2]!;
  const unit = SERVING_UNITS.find((u) => new RegExp(`^(?:${SPELLINGS[u]})$`, "i").test(word));
  return unit ? { amount: Math.round(amount * 100) / 100, unit } : null;
}

/**
 * The whole amount eaten, as words for the screen: 2 servings of 100 g is
 * "200g", and 2 servings of 1 cup is "2 cups".
 */
export function servingTotal(amount: number, unit: string, servings: number): string {
  const total = Math.round(amount * servings * 100) / 100;
  if (unit === "g" || unit === "ml") return `${total}${unit}`;
  if ((unit === "cup" || unit === "piece") && total > 1) return `${total} ${unit}s`;
  return `${total} ${unit}`;
}
